import { Button, Dialog, Text, VSpacer } from '@/components/DesignSystem';
import { ManufacturerAvatar } from "@/components/DesignSystem/Avatar/ManufacturerAvatar";
import { PointsTag } from '@/components/shared/PointsTag';
import { PromotionProductsBottomSheet } from '@/pages/Promotions/PromotionProductsBottomSheet';
import { QueryKeys } from '@/constants/QueryKeys';
import { LoyaltyPointsCapProgress } from '@/pages/Promotions/LoyaltyPointsCapProgress';
import { PromotionStatusChip } from '@/pages/Promotions/PromotionStatusChip';
import { PromotionApi } from '@/utilities/api/PromotionApi';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { CircularProgress, Divider, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import { SponsorType } from '@shared/enums';
import { sortBy } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

interface PromotionDetailsModalProps {
  onBack?: () => void,
  onClose: () => void,
  promotionId: string,
}

export const PromotionDetailsModal = ({
  onBack,
  onClose,
  promotionId,
}: PromotionDetailsModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data: promotion, isFetching } = useQuery(
    [QueryKeys.GET_PROMOTION, promotionId],
    () => PromotionApi.get(promotionId),
  );

  const [showRedeemProducts, setShowRedeemProducts] = useState(false);
  const [showEarnProducts, setShowEarnProducts] = useState(false);

  const getSponsorName = () => (
    promotion?.manufacturer?.name ?? promotion?.retailer?.name
  );

  const locationNameList = sortBy(
    promotion?.locations ?? [],
    ({ name }) => name,
  ).map((location) => location.name).join(', ');
  const productsEarnList = sortBy(
    promotion?.productsForEarningPoints ?? [],
    ({ name }) => name,
  );
  const productsRedeemList = sortBy(
    promotion?.productsForRedeemingPoints ?? [],
    ({ name }) => name,
  );
  const manufacturerName = promotion?.manufacturer?.name ?? '';
  const retailerName = promotion?.retailer?.name ?? '';

  const getProductRedeemText = () => {
    if (!promotion || productsRedeemList.length) {
      return undefined;
    }
    if (promotion.sponsorType === SponsorType.Retailer) {
      return t($ => $.promotions.details.allProducts, { retailerName });
    }
    return t($ => $.promotions.details.allManufacturerProducts, { manufacturerName, retailerName });
  };

  return (
    <Dialog
      acceptButton={(props) => (
        <Button {...props} color="inherit" onClick={onClose} variant="contained">
          {t($ => $.common.actions.close)}
        </Button>
      )}
      onClose={onClose}
      open
      secondaryActionButton={onBack ? (props) => (
        <Button {...props} onClick={onBack} startIcon={<ArrowBack />} variant="outlined">
          {t($ => $.common.actions.back)}
        </Button>
      ) : undefined}
      showCloseButton={false}
      testID="promotion-details-modal"
      title={t($ => $.promotions.details.title)}
    >
      {(isFetching || !promotion) ? (
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      ) : (
        <Stack>
          <Stack alignItems="center" p="0px 24px 16px 24px">
            {!!promotion.manufacturer && (
              <ManufacturerAvatar manufacturer={promotion.manufacturer} />
            )}
            <VSpacer size="2" />
            <Text category="headline-medium" textAlign="center">
              {getSponsorName()}
            </Text>
            {!!promotion.manufacturer && (
              <>
                <VSpacer size="2" />
                <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
                  {t($ => $.promotions.details.manufacturerPromotion)}
                </Text>
              </>
            )}
          </Stack>
          <Stack
            bgcolor="#393939"
            borderRadius="4px"
            color="white"
            py="8px"
            width="100%"
          >
            <Text category="label-medium" textAlign="center">
              {t(
                $ => $.promotions.card.runs,
                { startDate: promotion.startDate, endDate: promotion.endDate },
              )}
            </Text>
          </Stack>
          <VSpacer size="5" />
          <PromotionStatusChip promotion={promotion} />
          <VSpacer size="3" />
          <Text category="title-large" fontWeight={500}>
            {promotion.name}
          </Text>
          <VSpacer size="2" />
          <PointsTag
            points={promotion.value}
            rewardsType={promotion.rewardType}
            starHeight="25px"
            starWidth="25px"
            textCategory="body-large"
            uom={promotion.uom}
          />
          {!!promotion.pointCap && (
            <>
              <VSpacer size="3" />
              <LoyaltyPointsCapProgress promotion={promotion} />
            </>
          )}
          {promotion.overridePointsEngine && (
            <>
              <VSpacer size="3" />
              <Stack bgcolor={theme.palette.background.paper} borderRadius="4px" p="8px 12px">
                <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
                  {t($ => $.promotions.details.manufacturerPointsOnly)}
                </Text>
              </Stack>
            </>
          )}
          {promotion.grantsPointsManually && (
            <>
              <VSpacer size="3" />
              <Stack bgcolor={theme.palette.background.paper} borderRadius="4px" p="8px 12px">
                <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
                  {t($ => $.promotions.details.manualPointsOnly)}
                </Text>
              </Stack>
            </>
          )}
          {!!promotion.description && (
            <>
              <VSpacer size="5" />
              <Divider />
              <VSpacer size="5" />
              <Text category="title-medium">{t($ => $.promotions.details.promotionInfo)}</Text>
              <VSpacer size="3" />
              <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
                {promotion.description}
              </Text>
            </>
          )}
          {!promotion.allLocations && (
            <>
              <VSpacer size="5" />
              <Divider />
              <VSpacer size="5" />
              <Text category="title-medium">
                {t(
                  $ => $.promotions.details.participatingLocations,
                  { retailerName: promotion.retailer?.name },
                )}
              </Text>
              <VSpacer size="3" />
              <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
                {locationNameList}
              </Text>
            </>
          )}
          <VSpacer size="5" />
          <Divider />
          <VSpacer size="5" />
          <Button
            onClick={() => setShowEarnProducts(true)}
            sx={{ borderRadius: '4px' }}
            testID="product-earn-list-button"
          >
            {t($ => $.promotions.details.viewProductEarnList)}
          </Button>
          <VSpacer size="5" />
          <Button
            onClick={() => setShowRedeemProducts(true)}
            sx={{ borderRadius: '4px' }}
            testID="product-redeem-list-button"
          >
            {t($ => $.promotions.details.viewProductRedeemList)}
          </Button>
          <VSpacer size="5" />
        </Stack>
      )}
      {showEarnProducts && (
        <PromotionProductsBottomSheet
          onClose={() => setShowEarnProducts(false)}
          products={productsEarnList}
          variant="products"
        />
      )}
      {showRedeemProducts && (
        <PromotionProductsBottomSheet
          emptyText={getProductRedeemText()}
          onClose={() => setShowRedeemProducts(false)}
          products={productsRedeemList}
          variant="redeemable"
        />
      )}
    </Dialog>
  );
};
